/*
 * @Author: changdou.fe@bytedance.com
 * @Date: 2025-07-17 22:36:05
 * @LastEditors: changdou.fe@bytedance.com
 * @LastEditTime: 2026-04-08 17:33:19
 */
import BN from 'bignumber.js';
import numeral from 'numeral';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { chainList } from '@/app/const/chain';
import { ChainInfo, TokenInfo } from './dex-api';
import { formatPriceWithoutComma } from '@/app/perps/util';

export const truncateToDecimals = (value: number, decimal: number = 5) => {
  // 创建 BigNumber 实例（必须用字符串初始化以避免浮点误差）
  const bn = new BN(value.toString());
  // 设置保留指定位小数，并指定截断模式（ROUND_DOWN）
  return bn.decimalPlaces(decimal, BN.ROUND_DOWN).toFormat(decimal, { decimalSeparator: '.', groupSeparator: '' });
};

/**
 * 根据小数位数智能格式化数字
 * 如果小数位数小于等于 decimal，不做处理；如果大于 decimal，使用 toFixed 处理
 * @param value 要格式化的值（字符串或数字）
 * @param decimal 最大保留的小数位数
 * @returns 格式化后的字符串
 * @example
 * formatDecimalIfNeeded(0.01, 6) // "0.01"
 * formatDecimalIfNeeded(0.1234567, 6) // "0.123457"
 */
export const formatDecimalIfNeeded = (value: string | number, decimal: number = 6): string => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }

  const valueStr = typeof value === 'number' ? new BN(value.toString()).toString() : value;
  // 处理非数字情况
  if (isNaN(Number(valueStr))) {
    return valueStr;
  }

  // 如果没有小数点，直接返回
  if (!valueStr.includes('.')) {
    return valueStr;
  }

  // 获取小数部分
  const decimalPart = valueStr.split('.')[1];
  const currentDecimalPlaces = decimalPart ? decimalPart.length : 0;

  let result: string;
  // 如果小数位数小于等于 decimal，不做处理
  if (currentDecimalPlaces <= decimal) {
    result = valueStr;
  } else {
    // 如果小数位数大于 decimal，使用 toFixed 处理
    const num = parseFloat(valueStr);
    const formatted = num.toFixed(decimal);
    // 如果格式化后为0，返回原始数据
    if (parseFloat(formatted) === 0 && parseFloat(valueStr) !== 0) {
      return valueStr.replace(/\.?0+$/, '');
    }
    result = formatted;
  }

  // 去掉末尾无效的0和小数点
  return result.replace(/\.?0+$/, '');
};

export const smartFormat = (number: string, decimal: number = 2) => {
  const num = parseFloat(number);
  // 错误处理
  if (isNaN(num) || !isFinite(num)) {
    return '0';
  }
  const absNum = Math.abs(num);

  if (absNum >= 1000000000) {
    return numeral(num / 1000000000).format('0.00') + 'B'; // 十亿
  } else if (absNum >= 1000000) {
    return numeral(num / 1000000).format('0.00') + 'M'; // 百万
  } else if (absNum >= 1000) {
    return numeral(num / 1000).format('0.00') + 'K'; // 千
  }
  return numeral(num).format(`0.${'0'.repeat(decimal)}`);
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getChainByNetworkName = (networkName: string | undefined): ChainInfo | undefined => {
  if (!networkName) {
    return undefined;
  }
  const res = chainList.find(item => item.chainName.toLowerCase() === networkName?.toLowerCase());
  return res;
};

export const getTokenBalance = (
  allTokenBalances: {
    symbol: string;
    balance: string;
    tokenContractAddress: string;
  }[],
  token: TokenInfo
) => {
  // 1.优先使用tokenContractAddress进行判断
  // 2.如果tokenContractAddress为空，则使用tokenSymbol进行判断
  // 3.如果tokenContractAddress和tokenSymbol都为空，则使用isMainTokenSymbol进行判断
  const balance = allTokenBalances.find(
    t =>
      t.tokenContractAddress.toLowerCase() === token.tokenContractAddress.toLowerCase() ||
      (!t.tokenContractAddress && t.symbol === token.tokenSymbol) ||
      (token.isMainTokenSymbol && t.symbol === 'ETH')
  );
  if (!balance) {
    return '0';
  }
  return formatPriceWithoutComma({
    price: balance?.balance || '0',
    decimal: 6,
  });
};

export const getTokenBalanceWithUsdValue = (
  allTokenBalances: {
    symbol: string;
    balance: string;
    tokenContractAddress: string;
  }[],
  token: TokenInfo
): {
  balance: string;
  usdValue: string;
} => {
  const balance = allTokenBalances.find(
    t =>
      t.tokenContractAddress.toLowerCase() === token.tokenContractAddress.toLowerCase() ||
      (!t.tokenContractAddress && t.symbol === token.tokenSymbol) ||
      (token.isMainTokenSymbol && t.symbol === 'ETH')
  ) as unknown as TokenInfo;
  if (!balance) {
    return {
      balance: '0',
      usdValue: '0',
    };
  }
  return {
    balance: formatPriceWithoutComma({
      price: balance?.balance || '0',
      decimal: 6,
    }),
    usdValue: formatPriceWithoutComma({
      price: new BN(balance?.balance || '0').multipliedBy(balance?.tokenPrice || '0').toString() || '0',
      decimal: 6,
    }),
  };
};

export const divideForBn = (a: BN, b: BN, precision = 20) => {
  try {
    // 处理除数为零
    if (b.isZero()) {
      throw new Error('Division by zero');
    }
    // 执行除法，并保留指定精度
    return a.dividedBy(b).decimalPlaces(precision).toNumber();
  } catch (error) {
    return NaN;
  }
};

export const divide = (a: string, b: string, precision = 6) => {
  return divideForBn(new BN(a), new BN(b), precision);
};

export const multiply = (a: string, b: string, precision = 6) => {
  const aBn = new BN(a);
  const bBn = new BN(b);
  return aBn.times(bBn).toFixed(precision);
};

export const convertAddress = ({ str, pre = 6, suf = 4 }: { str: string; pre?: number; suf?: number }) => {
  if (!str) {
    return '';
  }
  // 如果太短直接返回
  if (str.length <= pre) {
    return str;
  }
  return `${str.slice(0, Number(pre))}...${str.slice(-Number(suf))}`;
};

// 缓存用户的选择
export const cacheSwapInfo = ({ chainId, tokenFrom, tokenTo }: { chainId: number; tokenFrom: TokenInfo; tokenTo: TokenInfo }) => {
  localStorage.setItem(
    'swap-chain-and-token',
    JSON.stringify({
      chainId,
      tokenFromAddress: tokenFrom.tokenContractAddress.toLowerCase(),
      tokenToAddress: tokenTo.tokenContractAddress.toLowerCase(),
    })
  );
};

// 数字类型input校验，增加一个是否允许输入负数的配置
export const validAmount = ({
  value,
  decimal = 6,
  allowNegative = false,
}: {
  value: string;
  decimal?: number; // 小数位数，默认是6
  allowNegative?: boolean; // 是否允许输入负数，默认不允许
}): { status: string; value: string; valueBn: BN } => {
  // 只允许数字和小数点；允许负数时可带前缀-
  const reg = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  if (!reg.test(value)) {
    return {
      status: 'error',
      value: '',
      valueBn: new BN(0),
    };
  }
  // 不允许输入负数
  if (!allowNegative && value.startsWith('-')) {
    return {
      status: 'error',
      value: '',
      valueBn: new BN(0),
    };
  }
  // 不允许输入多个小数点
  if ((value.match(/\./g) || []).length > 1) {
    return {
      status: 'error',
      value: '',
      valueBn: new BN(0),
    };
  }
  // 不允许多个0开头
  if (value.length > 1 && value.startsWith('0') && value[1] !== '.') {
    return {
      status: 'error',
      value: '',
      valueBn: new BN(0),
    };
  }
  // 最多只能输入6位小数
  if (value.includes('.')) {
    const decimalPart = value.split('.')[1];
    if (decimalPart && decimalPart.length > decimal) {
      return {
        status: 'error',
        value: '',
        valueBn: new BN(0),
      };
    }
  }
  const num = new BN(value);
  // 如果值等同于0，仅显示，但是则不查询，也不展示兑换详情内容
  if (!value || num.isZero()) {
    return {
      status: 'zero',
      valueBn: num,
      value,
    };
  }
  return {
    status: 'success',
    valueBn: num,
    value,
  };
};

export const formatNumberForComma = (number: string) => {
  if (!number) {
    return;
  }
  return numeral(number).format('0,0').replace(/,/g, ',');
};

export const scrollToTop = () => {
  const coRightSide = document.querySelector('.co-right-side');
  if (coRightSide) {
    coRightSide.scrollTo(0, 0);
  } else {
    window.scrollTo(0, 0);
  }
};

export const getChainInfo = (chainId: string | number) => {
  const chain = chainList.find(item => item.chainId === Number(chainId));
  return chain;
};

// WebView 专属路径，仅在这些路径下 isInAppWebView 才返回 true
const WEBVIEW_PATHS = ['/creator-weekly'];

export const isInAppWebView = () => {
  const hasBridge = typeof window !== 'undefined' && !!(window as any).BillionApp?.ready;
  const pathname = window.location.pathname;
  const isWebViewPath = WEBVIEW_PATHS.some(p => pathname.startsWith(p));
  return hasBridge && isWebViewPath;
};

/**
 * 校验目标钱包地址格式
 * - chainId 为 501（Solana）时使用 Base58 正则
 * - 其它链按 EVM 地址正则校验
 * 返回 i18n key，由调用方进行翻译
 */
export const isAddressValid = (address: string, chainId?: number): { status: boolean; msgKey: string } => {
  if (!address) {
    return {
      status: false,
      msgKey: 'exchange.addressCannotBeEmpty',
    };
  }
  const reg = chainId === 501 ? /^[1-9A-HJ-NP-Za-km-z]{44}$/ : /^0x[a-fA-F0-9]{40}$/;
  if (!reg.test(address)) {
    return {
      status: false,
      msgKey: 'exchange.invalidSolanaAddress',
    };
  }
  return {
    status: true,
    msgKey: '',
  };
};

/**
 * 格式化交易量
 */
export const formatVolume = (volume: number | undefined): string => {
  if (!volume) return '$0';
  if (volume >= 1000000) {
    return `$${(volume / 1000000).toFixed(1)}M`;
  } else if (volume >= 1000) {
    return `$${(volume / 1000).toFixed(0)}K`;
  }
  return `$${volume.toFixed(0)}`;
};

/**
 * 计算 24h 涨跌幅。结构化入参避免跨模块耦合 HLTokenInfo 类型
 */
export const calculate24hChange = (
  tokenInfo: { prevDayPx?: string | number | null; markPx?: string | number | null } | null,
) => {
  if (!tokenInfo?.prevDayPx || !tokenInfo?.markPx) {
    return { absolute: '0.0', percentage: '0.00%' };
  }
  const prevDayPrice = Number(tokenInfo.prevDayPx);
  const currentPrice = Number(tokenInfo.markPx);
  const absoluteChange = currentPrice - prevDayPrice;
  const percentageChange = (absoluteChange / prevDayPrice) * 100;
  const absoluteText = `${absoluteChange >= 0 ? '+' : ''}${absoluteChange.toFixed(5)}`;
  const percentageText = `${absoluteChange >= 0 ? '+' : ''}${percentageChange.toFixed(2)}%`;
  return {
    absolute: absoluteChange,
    absoluteText,
    percentage: percentageChange,
    percentageText,
  };
};

/**
 * 解码后端做 XSS 防护时对 string 字段做的 HTML 实体编码。
 * 主要场景：后端把 URL 里合法的 `&`（query 分隔符）错误地转义成 `&amp;`，
 * 导致前端拼出来的 URL 被 CDN/服务端当作未知参数。
 * 覆盖最常见的五种实体（&amp; &lt; &gt; &quot; &#39;）。
 */
export function decodeHtmlEntities(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
